import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, EmptyState, Select, Spinner, Typography } from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import { adminApiClient, type OrderNeedingCover } from '../lib/adminApiClient';
import { Pill } from '../components/Pill';
import { OrderLocationsPanel } from '../components/OrderLocationsPanel';

/**
 * Deliveries waiting for a Runner — the household's side of the cover queue.
 *
 * 🔒 Rod, 2026-09-12: a section for runs that need covering in the consumer
 * admin as well as the Runner admin, *"absolutely critical."* Two screens on
 * purpose, because they answer different questions:
 *
 *   Cover queue    — is anyone going to take this run? Who was offered it?
 *   THIS SCREEN    — which households are affected, what have they been told,
 *                    and what is their money doing?
 *
 * Somebody answering a household's message should not have to read a Runner
 * queue to find out whether that household was emailed.
 *
 * 🛑 READ-ONLY, on purpose. Everything that can be done already happens by
 * itself — the hold released when the Runner blocked the day, the household was
 * emailed then, the run was offered automatically, and an unclaimed offer is
 * cancelled before the delivery day. A button here would invent a manual path
 * around a marketplace Rod ruled automatic. Widening the search is a Runner-side
 * decision and lives on the Cover queue.
 *
 * 🪤 The two states are never merged into one list. "Waiting" is live and still
 * recoverable; "lost" already happened. Together, the urgent ones hide among
 * the closed ones.
 */
const GRID = '1.2fr 1.2fr 1fr 1.1fr 0.8fr';

type Filter = 'needs_cover' | 'no_cover' | 'all';

function clock(hhmm: string | null): string {
  if (!hhmm) return '';
  const h = Number(hhmm.slice(0, 2));
  const m = hhmm.slice(3, 5);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === '00' ? `${h12} ${suffix}` : `${h12}:${m} ${suffix}`;
}
function windowLabel(a: string | null, b: string | null): string {
  if (!a || !b) return '';
  const start = clock(a);
  const end = clock(b);
  return start.slice(-2) === end.slice(-2) ? `${start.slice(0, -3)}–${end}` : `${start}–${end}`;
}
function dayLabel(ymd: string | null): string {
  if (!ymd) return '—';
  return new Date(`${ymd}T00:00:00.000Z`).toLocaleDateString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC',
  });
}
function at(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
}
const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;

export default function OrdersNeedingCover() {
  const caps = useCapabilities();
  const canView = caps.has('user:read');
  const [filter, setFilter] = useState<Filter>('needs_cover');
  const [orders, setOrders] = useState<OrderNeedingCover[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiClient.listOrdersNeedingCover(filter);
      setOrders(res.orders);
      setCounts(res.counts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load these orders');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (canView) void load();
  }, [canView, load]);

  if (caps.loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><Spinner color="brand" /></Box>;
  if (!canView) return <Alert severity="error">You don’t have permission to view these orders.</Alert>;

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Deliveries needing a Runner</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Orders whose Runner booked time off. Each household was emailed and had its card hold released when it
        happened, and the run was offered automatically to every Runner who could take it. Widening that search
        lives on the Cover queue.
        {counts.needs_cover ? ` ${counts.needs_cover} still waiting.` : ''}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Select
          ariaLabel="Filter by state" label="State" native size="small" sx={{ width: 250 }} value={filter}
          onChange={(_e, v) => setFilter(v as Filter)}
          options={[
            { value: 'needs_cover', label: `Waiting for a Runner (${counts.needs_cover ?? 0})` },
            { value: 'no_cover', label: `Nobody covered it (${counts.no_cover ?? 0})` },
            { value: 'all', label: 'All' },
          ]}
        />
      </Box>

      {error && <Box sx={{ mb: 2 }}><Alert severity="error">{error}</Alert></Box>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><Spinner color="brand" /></Box>
      ) : orders.length === 0 ? (
        <EmptyState
          icon="onlineordering"
          title={filter === 'needs_cover' ? 'Every delivery has a Runner' : 'Nothing here'}
          body={
            filter === 'needs_cover'
              ? 'No household is waiting on us to find someone.'
              : 'No delivery matches this filter.'
          }
        />
      ) : (
        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', backgroundColor: '#fff' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 2, px: 2, py: 1.25, backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            {['Household', 'Delivery', 'What they were told', 'Money', 'State'].map((h, i) => (
              <Typography key={i} variant="caption" sx={{ fontWeight: 700, color: '#64748b', letterSpacing: 0.4 }}>{h.toUpperCase()}</Typography>
            ))}
          </Box>

          {orders.map((o, idx) => {
            const lost = o.fulfillmentStatus === 'cancelled';
            return (
              <Box key={o.orderId} sx={{ borderBottom: idx < orders.length - 1 ? '1px solid #eef2f6' : 'none' }}>
              <Box
                sx={{
                  display: 'grid', gridTemplateColumns: GRID, gap: 2, px: 2, py: 1.5, alignItems: 'start',
                  '&:hover': { backgroundColor: '#fafbfc' },
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 700, color: '#1f2937' }}>{o.household.name ?? '—'}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{o.household.email ?? '—'}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>{o.orderRef}</Typography>
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ color: '#1f2937', fontWeight: 600 }}>
                    {dayLabel(o.runDate)} · {windowLabel(o.deliveryWindowStart, o.deliveryWindowEnd)}
                  </Typography>
                  {o.runnerDisplayName && (
                    <Typography variant="caption" sx={{ color: '#15803d' }}>{o.runnerDisplayName} is covering</Typography>
                  )}
                </Box>

                {/*
                  🔑 The record of what this household has actually heard. Never
                  open a reply with something they have not been told.
                */}
                <Box>
                  {o.told.runnerUnavailableAt ? (
                    <Typography variant="caption" sx={{ display: 'block', color: '#1f2937' }}>
                      “Your Runner can’t make it” · {at(o.told.runnerUnavailableAt)}
                    </Typography>
                  ) : (
                    <Typography variant="caption" sx={{ display: 'block', color: '#b42318', fontWeight: 700 }}>
                      Not told yet
                    </Typography>
                  )}
                  {o.told.coveredAt && (
                    <Typography variant="caption" sx={{ display: 'block', color: '#15803d' }}>
                      “Someone is covering” · {at(o.told.coveredAt)}
                    </Typography>
                  )}
                  {o.told.cancelledAt && (
                    <Typography variant="caption" sx={{ display: 'block', color: '#64748b' }}>
                      “We could not cover it” · {at(o.told.cancelledAt)}
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ color: '#1f2937' }}>{money(o.money.estimatedTotalCents)}</Typography>
                  {/*
                    🔑 The answer to the first question a household asks. It is a
                    fact from the order row, never a reassurance somebody typed.
                  */}
                  <Typography variant="caption" sx={{ color: o.money.holdReleased ? '#15803d' : '#b42318', fontWeight: 700 }}>
                    {o.money.holdReleased ? 'Nothing charged' : `${money(o.money.capturedCents)} captured`}
                  </Typography>
                </Box>

                <Box>
                  <Pill
                    label={lost ? 'NOBODY COVERED' : 'WAITING'}
                    fg={lost ? '#b42318' : '#7A4A08'}
                    bg={lost ? '#fee4e2' : '#FDF3E1'}
                  />
                  {/* 🪤 The detail loads only when opened — one request per row
                      on a page nobody asked to expand is waste. */}
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setOpen(open === o.orderId ? null : o.orderId)}
                    sx={{
                      mt: 0.75, border: 'none', background: 'none', p: 0, cursor: 'pointer',
                      color: '#343F5B', fontWeight: 700, fontSize: '12px', fontFamily: 'inherit',
                      textDecoration: 'underline',
                    }}
                  >
                    {open === o.orderId ? 'Hide locations' : 'Where it was shopped'}
                  </Box>
                </Box>
              </Box>
              {open === o.orderId && (
                <Box sx={{ px: 2, pb: 2 }}>
                  <OrderLocationsPanel orderId={o.orderId} />
                </Box>
              )}
              </Box>
            );
          })}
        </Box>
      )}

      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 2 }}>
        Nothing on this screen needs doing — it is the record. To widen a search or offer a run to a Runner by
        name, use the Cover queue.
      </Typography>
    </Box>
  );
}
